/**
 * The multi-model table: hours down, sources across, and a dash wherever a
 * model has no step.
 *
 * It is the same data as the chart and it is also the chart's **accessible
 * form** — `Chart.svelte` is `role="img"` and points at this with
 * `aria-describedby`, which is a better answer than a wall of `<desc>` and
 * costs nothing because the table exists anyway.
 *
 * The rule that shapes it: **nothing is resampled.** The rows are the hours in
 * the window and a 3-hourly GFS fills two of every six, with a dash in the
 * rest. Interpolating an instantaneous field would be defensible and
 * interpolating an accumulation would not, and a rule with an exception in it
 * is a rule the third person to read it applies wrong.
 */

import type { CompareSeries } from '../api/compare'
import { blend, OFFICIAL_BLEND_ID, valuesByInstant, type BlendCell } from './blend'

export interface Column {
  id: string
  label: string
  kind: 'model' | 'official' | 'observed' | 'blend'
  /** How many models a blend cell averaged, per row. Empty for a real series. */
  cells: (number | null)[]
  counts?: (number | null)[]
}

export interface TableModel {
  stamps: number[]
  columns: Column[]
}

/**
 * The instants the table has rows for: every stamp any *visible* series
 * actually publishes, inside the window.
 *
 * Not a generated hourly range. A sub-hourly HRRR publishes at :15 and a
 * 3-hourly GFS at 00Z, and inventing an hourly grid for them would put a
 * dash-filled row wherever nothing lands and quietly hide the 15-minute steps
 * between two hours.
 */
export function stampsOf(
  series: readonly CompareSeries[],
  window: { from: number; to: number },
  hidden: ReadonlySet<string> = new Set(),
): number[] {
  const found = new Set<number>()
  for (const s of series) {
    if (hidden.has(s.id) || s.kind === 'observed') continue
    for (const point of s.points) {
      const at = Date.parse(point.t)
      if (!Number.isNaN(at) && at >= window.from && at <= window.to) found.add(at)
    }
  }
  return [...found].sort((a, b) => a - b)
}

/**
 * The table, as columns.
 *
 * Observations are left out on purpose: they are a measurement in the past and
 * the table's job is the forecast the models disagree about. The chart draws
 * them, where the time axis makes "this already happened" obvious.
 */
export function buildTable(
  series: readonly CompareSeries[],
  window: { from: number; to: number },
  hidden: ReadonlySet<string> = new Set(),
): TableModel {
  const stamps = stampsOf(series, window, hidden)
  const columns: Column[] = []

  for (const s of series) {
    if (s.kind === 'observed' || hidden.has(s.id)) continue
    const at = valuesByInstant(s)
    columns.push({
      id: s.id,
      label: s.kind === 'model' && s.id === OFFICIAL_BLEND_ID ? `${s.label} (blend)` : s.label,
      kind: s.kind,
      cells: stamps.map((stamp) => at.get(stamp) ?? null),
    })
  }

  const naive = blend(series, stamps, hidden)
  if (naive.some((cell) => cell !== null)) {
    columns.push({
      id: 'blend',
      label: 'Mean',
      kind: 'blend',
      cells: naive.map((cell: BlendCell | null) => (cell ? cell.value : null)),
      counts: naive.map((cell) => (cell ? cell.n : null)),
    })
  }
  return { stamps, columns }
}

/**
 * The column's name in the header, where eight columns share ~330 px.
 *
 * The full label is still the header's accessible name, so a screen reader
 * hears "NBM 2.5 km (blend)" where the eye reads "NBM".
 */
const SHORT: Record<string, string> = {
  hrrr: 'HRRR',
  'hrrr-subh': '15m',
  rap: 'RAP',
  nam: 'NAM',
  nbm: 'NBM',
  gfs: 'GFS',
}

export function shortLabel(column: Pick<Column, 'id' | 'kind' | 'label'>): string {
  if (column.kind === 'official') return 'NWS'
  if (column.kind === 'blend') return column.label
  return SHORT[column.id] ?? column.label.split(' ')[0]
}
